import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import Loader from "../../../components/partials/components/loader";
import { getApiCall } from "../../../service/getApiCall";
import { postApiCall } from "../../../service/postApiCall";
import { toast } from "react-toastify";
import {
  API_MANAGE_GROUP_LISTING, // /vX/groups
  API_COMPANY_LIST,           // /vX/company
  API_BULK_GROUP_DELETE,    // /vX/groups/bulk-delete
  API_GET_COMPANY_GROUP
} from "../../../service/url";

export default function BulkGroupDelete() {
  const [loading, setLoading] = useState(false);

  // master lists
  const [groups, setGroups] = useState([]);     // [{group_id, group_name}]
  const [allCompany, setAllCompany] = useState([]); // full company list from API

  // working (filtered) list shown in table
  const [company, setCompany] = useState([]);

  // UI state
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState(10);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [groupName, setGroupName] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [groupsRes, companyRes] = await Promise.all([
          getApiCall(API_MANAGE_GROUP_LISTING, {}),
          getApiCall(API_COMPANY_LIST, {}),
        ]);
        setGroups(Array.isArray(groupsRes) ? groupsRes : []);
        setAllCompany(Array.isArray(companyRes) ? companyRes : []);
        setCompany([]);
      } catch (err) {
        toast.error("Failed to load groups/company");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter button: show only members that belong to the selected group
  const applyGroupFilter = async() => {
    if (!selectedGroupId) {
      toast.error("Please select a group first.");
      return;
    }

    const gid = String(selectedGroupId);
    try {
      const groupCompany = await getApiCall(
        API_GET_COMPANY_GROUP(gid),
        {}
      );

      const companyIds = groupCompany.map(item => Number(item.company_id));

      const companyIdSet = new Set(companyIds);

      const filtered = (allCompany || []).filter(company =>
        companyIdSet.has(Number(company.id))
      );

      const group = (groups || []).find(
        (loc) => Number(loc.group_id) === Number(selectedGroupId)
      );

      setGroupName(group?.group_name)
      setCompany(filtered);
      setSelectedCompanyIds([]);
    } catch (error) {
      console.error("Error fetching company location", error);
    }
  };

  // Clear filter to empty list (as in your UI)
  const clearFilter = () => {
    setCompany([]);
    setSelectedCompanyIds([]);
    setSearchValue("");
  };

  // search within the filtered list
  const searched = useMemo(
    () =>
      company.filter((u) =>
        (u.name || "").toLowerCase().includes(searchValue.toLowerCase())
      ),
    [company, searchValue]
  );

  // Checkbox toggles
  const toggleCompany = (companyId) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };

  // Select-all respects current search
  const toggleAll = () => {
    const visibleIds = searched.map((u) => u.id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedCompanyIds.includes(id));

    if (allSelected) {
      setSelectedCompanyIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedCompanyIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedGroupId) {
      toast.error("Please select a group first.");
      return;
    }
    if (selectedCompanyIds.length === 0) {
      toast.error("Select at least one Company.");
      return;
    }

    try {
      setButtonDisable(true);
      const payload = { company_ids: selectedCompanyIds, group_id: selectedGroupId };
      const res = await postApiCall(API_BULK_GROUP_DELETE, payload);

      if (res) {
        toast.success(res.message || "Company removed from group successfully");
        // refresh by re-applying filter (so removed Company disappear)
        applyGroupFilter();
        setSelectedCompanyIds([]);
      } else {
        toast.error(res?.message || "Failed to remove company");
      }
    } catch (err) {
      toast.error(err?.message || "Error removing company from group");
    } finally {
      setButtonDisable(false);
      setConfirmVisible(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title">Bulk Company Group Deletion</h4>
            </Card.Header>

            <Card.Body className="px-0">
              {/* Control row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px",
                  flexWrap: "wrap",
                }}
              >
                {/* Show X items */}
                <span>Show</span>
                <Dropdown
                  value={rows}
                  options={[
                    { label: "5", value: 5 },
                    { label: "10", value: 10 },
                    { label: "20", value: 20 },
                    { label: "50", value: 50 },
                  ]}
                  onChange={(e) => setRows(e.value)}
                  style={{ width: 90, height: 40, display: "flex", alignItems: "center" }}
                />
                <span>items</span>

                {/* Search */}
                <Form.Control
                  style={{ width: 250, height: 40, marginLeft: 10 }}
                  placeholder="Search"
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <Button label="Clear" className="p-button-secondary" onClick={clearFilter} />

                {/* Group selector + Filter */}
                <Dropdown
                  value={selectedGroupId}
                  options={groups.map((g) => ({ label: g.group_name, value: g.group_id }))}
                  onChange={(e) => setSelectedGroupId(e.value)}
                  placeholder="Select Group"
                  style={{ width: 260, marginLeft: 10 }}
                />
                <Button  label="Filter" className="p-button-secondary" onClick={applyGroupFilter} />

                {/* Delete */}
                <Button
                  label="Delete Selected"
                  className="p-button-danger"
                  disabled={selectedCompanyIds.length === 0 || !selectedGroupId || buttonDisable}
                  onClick={() => setConfirmVisible(true)}
                />
              </div>

              {/* Table */}
              <div className="scroll-container">
                <DataTable
                  value={searched}
                  paginator
                  rows={rows}
                  tableStyle={{ minWidth: "50rem" }}
                  emptyMessage="No data available"
                >
                  <Column
                    header={
                      <Checkbox
                        checked={
                          searched.length > 0 &&
                          searched.every((u) => selectedCompanyIds.includes(u.id))
                        }
                        onChange={toggleAll}
                      />
                    }
                    headerStyle={{ width: "3rem" }}
                    body={(row) => (
                      <Checkbox
                        checked={selectedCompanyIds.includes(row.id)}
                        onChange={() => toggleCompany(row.id)}
                      />
                    )}
                  />
                  <Column field="id" header="Company Id" sortable />
                  <Column field="name" header="Company Name" sortable />
                  <Column header="Group Name" body={groupName} sortable />
                </DataTable>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm Modal */}
      <Dialog
        header="Confirm Bulk Delete"
        visible={confirmVisible}
        style={{ width: "25vw" }}
        onHide={() => setConfirmVisible(false)}
        footer={
          <div>
            <Button label="Cancel" className="p-button-text" onClick={() => setConfirmVisible(false)} />
            <Button
              label="Confirm"
              className="p-button-danger"
              onClick={handleBulkDelete}
              disabled={buttonDisable}
            />
          </div>
        }
      >
        <p>Are you sure you want to remove {selectedCompanyIds.length} Company(s) from this group?</p>
      </Dialog>
    </div>
  );
}
